import { filter, flatMap } from "lodash";
import { Interval } from "@linkfluence/models/date";
import { Platform } from "@linkfluence/models/platform";
import { Role } from "@linkfluence/models/role";
import { Milestone } from "@radarly/models/milestone";
import { Corpora } from "./corpora";
import { CustomField } from "./custom-field";
import { Group } from "./groups";
import { Query } from "./queries";
import { Credential } from "./social";
import {
  FacebookSocialAccount,
  InstagramSocialAccount,
  SinaweiboSocialAccount,
  TwitterSocialAccount,
  YoutubeSocialAccount,
} from "./social_accounts";
import { Workspace } from "./workspace";

export interface Flags {
  hasDedicatedIndex?: boolean;
  hasPaidSSE?: boolean;
  isAccessible?: boolean;
  isActive?: boolean;
  isAlcmeonActivated?: boolean;
  isBoardreaderActivated?: boolean;
  isCharged?: boolean;
  isCom?: boolean;
  isGnipActivated?: boolean;
  isMigrated?: boolean;
  isResearch?: boolean;
  isSinaweiboActivated?: boolean;
  isSinaweiboCommentsActivated?: boolean;
  isSinaweiboForwardsActivated?: boolean;
  isSinaweiboLiveCommentsActivated?: boolean;
  isSocialWallActivated?: boolean;
  isSocialgistCorpusReviewActivated?: boolean;
  isSocialgistReviewActivated?: boolean;
  isSocialgistVKontakteActivated?: boolean;
  isTest?: boolean;
  isPublic?: boolean;
  isTranslationActivated?: boolean;
  isEmvCardEnabled?: boolean;
}

export type Id = number;

interface bBenchmarkEntity {
  created: string;
  description: string;
  facebookPages: string[];
  id: number;
  imageUrl: string;
  instagramAccounts: string[];
  label: string;
  pictureId: number;
  projectId: number;
  sinaweiboAccounts: string[];
  twitterAccounts: string[];
  updated: string;
}

interface GeoLocation {
  address: string;
  color: string;
  id: number;
  imageUrl: string;
  label: string;
  latitude: number;
  longitude: number;
  pictureId: number;
  projectId: number;
  radius: number;
  tags: number[];
}

interface GeoTag {
  color: string;
  id: number;
  imageUrl: string;
  label: string;
  pictureId: number;
}

export interface BlockedAuthor {
  created: string;
  id: string;
  name: string;
  reversedUrl?: string; // Only for web sources
}

export type BlockedAuthors = Record<Platform, BlockedAuthor[]>;

export interface Project {
  accountId?: number; // Account Id
  alcmeonCompany?: string; // Internal id used by Alcemon
  benchmarkEntities?: bBenchmarkEntity[]; // Benchmark Entities
  brandLogos?: number[]; // Logo detections
  clientReference?: string; // Client reference
  corpora?: Corpora[];
  created?: string; // Creation date
  credentials?: Partial<Record<Platform, Credential[]>>;
  dashboards?: Workspace[];
  dataFilter?: {
    sinaweiboComments: boolean;
    sinaweiboForwards: boolean;
  };
  docsCount?: number;
  docsVersion?: number;
  flags?: Flags;
  focuses?: Query[];
  geoArea?: {
    geoLocations: GeoLocation[];
    geoTags: GeoTag[];
  };
  id: number;
  industries?: any; // TODO
  label?: string;
  limits?: {
    maxEntities: number;
    maxFocus: number;
    maxLiveStreams: number;
    maxQueryLength: number;
    maxSearch: number;
    maxSocialAccountByPlaform: number;
    maxSocialAccounts: number;
    maxTopics: number;
    maxTranslationBudget: number;
    maxVol: number;
    mutualizedQuery: string;
    pack: string;
    renew: string;
    start: string;
    stop: string;
  };
  milestones?: Milestone[];
  mm3Id?: number;
  outOfReachCount?: number;
  personIdentifications?: number[];
  pictureId?: number;
  imageUrl?: string;
  platforms?: Platform[];
  projectManagerId?: number;
  renew?: number;
  researchManagerId?: number;
  sfdcIds?: { id: string }[];
  socialAccounts?: {
    dailymotion: any[];
    facebook: FacebookSocialAccount[];
    ganalytics: any[];
    gplus: any[];
    instagram: InstagramSocialAccount[];
    sinaweibo: SinaweiboSocialAccount[];
    twitter: TwitterSocialAccount[];
    youtube: YoutubeSocialAccount[];
  };
  socialWallText?: string;
  start?: string;
  stop?: string;
  tags?: {
    custom: CustomField[];
    user: Group[];
  };
  totalIndexedDocsCount?: number;
  updated?: string;
  role?: Role;
  picture?: string;
}

export interface ProjectWithCorpora {
  id: Id;
  label?: string;
  pictureId: number;
  role: Role;
  platforms: Platform[];
  corpora: Corpora[];
}

export interface WeiboStatus {
  frozen: boolean;
  freezeList: {from: string, to: string}[];
}

export function getDefault(): Project {
  return {
    id: 0,
  };
}

export function getAlcmeonId(project: Project) {
  return project ? project.alcmeonCompany : null;
}

export function getProjectLimits(
  project: Project,
  interval: Interval,
  isMaxToday: boolean = false
) {
  const { start, stop } = project.limits;
  const limitTo = new Date(stop);
  const today = new Date();
  return {
    from: new Date(start),
    to: isMaxToday ? (limitTo >= today ? today : limitTo) : limitTo,
    interval,
  };
}

export function getProjectCredentials(project: Project, platform: string) {
  if (!project.credentials || !project.socialAccounts) {
    return [];
  }
  if (platform === Platform.Instagram) {
    const socialAccounts = project?.socialAccounts[Platform.Instagram];
    const ownSocialAccounts = filter(socialAccounts, { relationType: "me" });
    const socialCreds = flatMap(ownSocialAccounts, "credentialIds");
    const instagramCreds = filter(
      project.credentials[Platform.Facebook],
      (credential) => socialCreds.includes(credential.id)
    );
    return instagramCreds;
  } else {
    return project.credentials[platform];
  }
}
